import {
  CanvasMigrationJobView,
  MIGRATION_STATUSES,
  MigrationStatus,
  MigrationTaskView,
} from './canvasMigrationTypes'

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {}

const readString = (value: unknown): string =>
  typeof value === 'string' ? value : ''

const readNumber = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0

const readOptionalNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

const readStatus = (value: unknown): MigrationStatus =>
  MIGRATION_STATUSES.includes(value as MigrationStatus)
    ? (value as MigrationStatus)
    : 'pending'

const clampPercent = (value: number): number =>
  Math.min(100, Math.max(0, Math.round(value)))

/**
 * Spec errors are `{ code, message } | null`. Older mocks may still send a
 * plain string — accept both so the console never blanks out.
 */
const readError = (
  value: unknown
): { errorCode: string; errorMessage: string } => {
  if (value == null) {
    return { errorCode: '', errorMessage: '' }
  }
  if (typeof value === 'string') {
    return { errorCode: '', errorMessage: value }
  }
  const record = asRecord(value)
  return {
    errorCode: readString(record.code),
    errorMessage: readString(record.message) || readString(record.code),
  }
}

/**
 * Turns a snake_case phase key such as `updating_marketplace` into a readable
 * label such as `Updating marketplace`.
 */
export const formatPhaseLabel = (phase: string): string => {
  const words = phase.replace(/[_-]+/g, ' ').trim()
  if (!words) {
    return ''
  }
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase()
}

/** Renders `20,000 / 43,555 rows`, or an empty string when counts are absent. */
export const formatRowCount = (
  processedRows: number | null,
  totalRows: number | null
): string => {
  if (processedRows === null || totalRows === null) {
    return ''
  }
  return `${processedRows.toLocaleString('en-US')} / ${totalRows.toLocaleString(
    'en-US'
  )} rows`
}

const parseTask = (payload: unknown): MigrationTaskView => {
  const task = asRecord(payload)
  const phaseProgress = asRecord(task.phase_progress)
  const phase = readString(task.phase)
  const { errorCode, errorMessage } = readError(task.error)

  return {
    taskId: readString(task.task_id),
    type: readString(task.type),
    name: readString(task.name) || formatPhaseLabel(readString(task.type)),
    accountId: readString(task.account_id),
    status: readStatus(task.status),
    phase,
    phaseLabel: formatPhaseLabel(phase),
    phasePercentage: clampPercent(readNumber(phaseProgress.percentage)),
    processedRows: readOptionalNumber(phaseProgress.processed_rows),
    totalRows: readOptionalNumber(phaseProgress.total_rows),
    progress: clampPercent(readNumber(task.progress)),
    errorMessage,
    errorCode,
    createdAt: readString(task.created_at),
  }
}

const createdAtMillis = (createdAt: string): number => {
  const time = Date.parse(createdAt)
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time
}

/**
 * Oldest task first. Equal timestamps fall back to task id so a poll that
 * returns the same tasks in a different order cannot reshuffle the grid.
 */
export const sortMigrationTasks = (
  tasks: MigrationTaskView[]
): MigrationTaskView[] =>
  [...tasks].sort((left, right) => {
    const byCreated =
      createdAtMillis(left.createdAt) - createdAtMillis(right.createdAt)
    if (byCreated !== 0) {
      return byCreated
    }
    return left.taskId.localeCompare(right.taskId)
  })

export const createEmptyMigrationJob = (): CanvasMigrationJobView => ({
  jobId: '',
  partnerId: '',
  status: 'pending',
  progress: 0,
  errorMessage: '',
  errorCode: '',
  tasks: [],
  hasJob: false,
  isRunning: false,
  hasFailure: false,
})

/**
 * Normalises the control-plane Job Status payload into the shape the console
 * renders. Unknown or malformed payloads degrade to an empty job rather than
 * throwing, so a single bad poll cannot break the page.
 */
export const parseCanvasMigrationResponse = (
  payload: unknown
): CanvasMigrationJobView => {
  const body = asRecord(payload)
  const jobId = readString(body.job_id)

  if (!jobId) {
    return createEmptyMigrationJob()
  }

  const status = readStatus(body.status)
  const tasks = sortMigrationTasks(
    Array.isArray(body.tasks) ? body.tasks.map(parseTask) : []
  )
  const { errorCode, errorMessage } = readError(body.error)

  return {
    jobId,
    partnerId: readString(body.partner_id),
    status,
    progress: clampPercent(readNumber(body.progress)),
    errorMessage,
    errorCode,
    tasks,
    hasJob: true,
    isRunning: status === 'in_progress' || status === 'pending',
    hasFailure:
      status === 'failed' || tasks.some(task => task.status === 'failed'),
  }
}
